'use client';

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Shield, Clock, Brush, Calendar, Coffee, Wrench } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';
import { useRTL } from '@/hooks/useRTL';

gsap.registerPlugin(ScrollTrigger);


export default function LongevityCareSection() {
    const { t } = useTranslation('dental_veneers');
    const { isRTL } = useRTL();
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const tipsRef = useRef<HTMLDivElement>(null);

    const maintenanceTips = [
        {
            icon: Brush,
            title: t('longevity.careGuidelines.oralHygiene.title'),
            description: t('longevity.careGuidelines.oralHygiene.description')
        },
        {
            icon: Calendar,
            title: t('longevity.careGuidelines.regularCheckups.title'),
            description: t('longevity.careGuidelines.regularCheckups.description')
        },
        {
            icon: Shield,
            title: t('longevity.careGuidelines.avoidHardFoods.title'),
            description: t('longevity.careGuidelines.avoidHardFoods.description')
        },
        {
            icon: Wrench,
            title: t('longevity.careGuidelines.nightguard.title'),
            description: t('longevity.careGuidelines.nightguard.description')
        },
        {
            icon: Coffee,
            title: t('longevity.careGuidelines.limitStaining.title'),
            description: t('longevity.careGuidelines.limitStaining.description')
        },
        {
            icon: Clock,
            title: t('longevity.careGuidelines.avoidAsTools.title'),
            description: t('longevity.careGuidelines.avoidAsTools.description')
        }
    ];

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Set initial states
            gsap.set(titleRef.current, { opacity: 0, y: 50 });
            gsap.set(contentRef.current, { opacity: 0, x: -30 });

            if (tipsRef.current?.children) {
                gsap.set(tipsRef.current.children, { opacity: 0, y: 40, scale: 0.9 });
            }

            // Title animation with scroll trigger
            gsap.to(titleRef.current, {
                opacity: 1,
                y: 0,
                duration: 1,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: titleRef.current,
                    start: "top 80%",
                    end: "bottom 20%",
                    toggleActions: "play none none reverse",
                    scrub: false
                }
            });

            // Content animation
            gsap.to(contentRef.current, {
                opacity: 1,
                x: 0,
                duration: 0.8,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: contentRef.current,
                    start: "top 75%",
                    end: "bottom 25%",
                    toggleActions: "play none none reverse",
                    scrub: false
                }
            });

            // Individual tip card animations - each card appears based on its own scroll position
            if (tipsRef.current?.children) {
                Array.from(tipsRef.current.children).forEach((tip, index) => {
                    gsap.to(tip, {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        duration: 0.9,
                        ease: "power3.out",
                        scrollTrigger: {
                            trigger: tip,
                            start: "top 85%", // Tip becomes visible when its top hits 85% of viewport
                            end: "bottom 15%",
                            toggleActions: "play none none reverse",
                            scrub: false
                        }
                    });
                });
            }

        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <section
            id="longevity"
            ref={sectionRef}
            className="py-20 bg-slate-50 dark:bg-navy-950 relative overflow-hidden"
        >
            {/* Background decoration */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute top-20 left-10 w-64 h-64 bg-gold rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 right-10 w-80 h-80 bg-gold rounded-full blur-2xl"></div>
            </div>

            <div className="container relative">
                {/* Section Header */}
                <div className="text-center max-w-4xl mx-auto mb-16">
                    <h2
                        ref={titleRef}
                        className={cn(
                            "text-4xl md:text-5xl lg:text-6xl font-bold text-navy dark:text-white mb-8 font-serif",
                            isRTL && "font-arabic"
                        )}
                    >
                        {t('longevity.title')}
                    </h2>
                    <p className={cn(
                        "text-xl text-slate-600 dark:text-slate-300 leading-relaxed",
                        isRTL && "font-arabic"
                    )}>
                        {t('longevity.subtitle')}
                    </p>
                </div>

                <div className="grid lg:grid-cols-2 gap-16 items-start">
                    {/* Left Content - Longevity Information */}
                    <div ref={contentRef} className="space-y-8">
                        {/* Longevity Summary */}
                        <div className="bg-gold/10 rounded-2xl p-8 border border-gold/20">
                            <div className="relative mb-6">
                                <div className={cn(
                                    "w-12 h-12 bg-gold rounded-xl flex items-center justify-center absolute top-0",
                                    isRTL ? "right-0" : "left-0"
                                )}>
                                    <Clock className="w-6 h-6 text-white" />
                                </div>
                                <div className={cn(
                                    "w-full",
                                    isRTL ? "pr-16" : "pl-16"
                                )}>
                                    <h3 className={cn(
                                        "text-2xl font-bold text-navy dark:text-white",
                                        isRTL && "font-arabic text-right"
                                    )}>
                                        {t('longevity.expectedLifespan.title')}
                                    </h3>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                                    <h4 className={cn(
                                        "text-lg font-bold text-navy dark:text-white mb-2",
                                        isRTL && "font-arabic text-right"
                                    )}>
                                        {t('longevity.expectedLifespan.porcelain.title')}
                                    </h4>
                                    <p className={cn(
                                        "text-slate-600 dark:text-slate-300",
                                        isRTL && "font-arabic text-right"
                                    )}>
                                        {t('longevity.expectedLifespan.porcelain.duration')}
                                    </p>
                                </div>

                                <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                                    <h4 className={cn(
                                        "text-lg font-bold text-navy dark:text-white mb-2",
                                        isRTL && "font-arabic text-right"
                                    )}>
                                        {t('longevity.expectedLifespan.composite.title')}
                                    </h4>
                                    <p className={cn(
                                        "text-slate-600 dark:text-slate-300",
                                        isRTL && "font-arabic text-right"
                                    )}>
                                        {t('longevity.expectedLifespan.composite.duration')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Content - Maintenance Tips */}
                    <div className="space-y-8">
                        <div className="text-center lg:text-left">
                            <h3 className={cn(
                                "text-3xl font-bold text-navy dark:text-white mb-4",
                                isRTL && "font-arabic text-right"
                            )}>
                                {t('longevity.careGuidelines.title')}
                            </h3>
                            <p className={cn(
                                "text-slate-600 dark:text-slate-300 text-lg",
                                isRTL && "font-arabic text-right"
                            )}>
                                {t('longevity.careGuidelines.subtitle')}
                            </p>
                        </div>

                        <div ref={tipsRef} className="space-y-4">
                            {maintenanceTips.map((tip, index) => (
                                <motion.div
                                    key={index}
                                    whileHover={{ scale: 1.02, x: 5 }}
                                    transition={{ duration: 0.2 }}
                                    className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700 hover:border-gold/50 shadow-sm transition-all duration-300 group"
                                >
                                    <div className="relative">
                                        <div className={cn(
                                            "w-12 h-12 bg-gold rounded-xl flex items-center justify-center absolute top-0 group-hover:scale-110 transition-transform duration-300",
                                            isRTL ? "right-0" : "left-0"
                                        )}>
                                            <tip.icon className="w-6 h-6 text-white" />
                                        </div>
                                        <div className={cn(
                                            "w-full",
                                            isRTL ? "pr-16" : "pl-16"
                                        )}>
                                            <h4 className={cn(
                                                "text-lg font-bold text-navy dark:text-white mb-2 group-hover:text-gold dark:group-hover:text-gold transition-colors duration-300",
                                                isRTL && "font-arabic text-right"
                                            )}>
                                                {tip.title}
                                            </h4>
                                            <p className={cn(
                                                "text-slate-600 dark:text-slate-300 leading-relaxed",
                                                isRTL && "font-arabic text-right"
                                            )}>
                                                {tip.description}
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
} 
